import { t } from "i18next";
import moment from "moment";

import {
  GetResellerCommissionsRequestParams,
  requestGetResellerCommissions
} from "../apis";
import {
  COMMISSIONS_EXPORT_MAX_PAGES,
  COMMISSIONS_EXPORT_PAGE_SIZE
} from "../constants";
import { ResellerCommission } from "../resellers.types";

const CSV_HEADERS = [
  "Order",
  "Date",
  "Customer",
  "Phone Number",
  "Order Amount",
  "Commission Source",
  "Code",
  "Commission",
  "Rate",
  "Status",
  "Paid at"
];

const escapeCsvValue = (value: string | number) => {
  const stringified = String(value ?? "");

  return `"${stringified.replace(/"/g, '""')}"`;
};

const toCsvRow = (commission: ResellerCommission) =>
  [
    commission.orderId,
    moment(commission.orderCreatedAt).format("DD/MM/YYYY HH:mm"),
    `${commission.buyer.firstname} ${commission.buyer.lastname}`,
    commission.buyer.phoneNumber,
    commission.orderAmount,
    t(commission.source),
    commission.origin?.code ?? "",
    commission.commissionAmount,
    commission.commissionRatePct
      ? `${Math.round(commission.commissionRatePct)}%`
      : "",
    t(commission.status),
    commission.paidAt ? moment(commission.paidAt).format("DD/MM/YYYY") : ""
  ]
    .map(escapeCsvValue)
    .join(";");

export interface ExportResellerCommissionsResult {
  exportedCount: number;
  /** True when the page-walk hit its ceiling and the file is not the full set. */
  isTruncated: boolean;
}

/**
 * There is no server-side export: the endpoint is capped at 50 rows a page, so a
 * full export means walking the pages from here. The walk is bounded, and the
 * caller is told when the file stops short of everything.
 */
export const exportResellerCommissionsToCsv = async (
  resellerId: string,
  params: GetResellerCommissionsRequestParams,
  fileName: string
): Promise<ExportResellerCommissionsResult> => {
  const rows: ResellerCommission[] = [];

  let page = 1;
  let hasNextPage = true;

  while (hasNextPage && page <= COMMISSIONS_EXPORT_MAX_PAGES) {
    // Sequential on purpose: an influencer with hundreds of orders would
    // otherwise fire dozens of parallel requests at the API for one click.
    // eslint-disable-next-line no-await-in-loop
    const response = await requestGetResellerCommissions(resellerId, {
      ...params,
      page,
      take: COMMISSIONS_EXPORT_PAGE_SIZE
    });

    rows.push(...response.data);
    hasNextPage = response.meta.hasNextPage;
    page += 1;
  }

  const csv = [
    CSV_HEADERS.map((header) => escapeCsvValue(t(header))).join(";"),
    ...rows.map(toCsvRow)
  ].join("\n");

  // The BOM is what makes Excel read the accents in French names correctly.
  const blob = new Blob([`﻿${csv}`], {
    type: "text/csv;charset=utf-8;"
  });
  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(objectUrl);

  return { exportedCount: rows.length, isTruncated: hasNextPage };
};
